import { useState, useMemo, useCallback } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useProjects } from "@/hooks/useProjects";
import { useCreateTimeEntry } from "@/hooks/useTimeEntries";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { CategorySelect } from "@/components/timer/CategorySelect";
import type { Database } from "@/integrations/supabase/types";

type TimeCategory = Database["public"]["Enums"]["time_category"];

interface ManualEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManualEntryDialog({ open, onOpenChange }: ManualEntryDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: projects } = useProjects();
  const createTimeEntry = useCreateTimeEntry();

  const [projectId, setProjectId] = useState<string>("");
  const [category, setCategory] = useState<TimeCategory>("dev");
  const [customCategory, setCustomCategory] = useState<string>("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [isBillable, setIsBillable] = useState(true);
  const [hourlyRate, setHourlyRate] = useState("");

  const resetForm = useCallback(() => {
    setProjectId("");
    setCategory("dev");
    setCustomCategory("");
    setDescription("");
    setDate(new Date());
    setStartTime("09:00");
    setEndTime("10:00");
    setIsBillable(true);
    setHourlyRate("");
  }, []);

  // Reseta o form quando o dialog é fechado
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    },
    [onOpenChange, resetForm],
  );

  const handleSave = async () => {
    if (!user?.id) return;

    if (!projectId) {
      toast({ variant: "destructive", title: "Erro", description: "Selecione um projeto." });
      return;
    }

    if (!startTime || !endTime) {
      toast({ variant: "destructive", title: "Erro", description: "Informe os horários de início e fim." });
      return;
    }

    // Validar hourlyRate (NaN check)
    if (hourlyRate && (isNaN(parseFloat(hourlyRate)) || parseFloat(hourlyRate) < 0)) {
      toast({ variant: "destructive", title: "Erro", description: "Taxa por hora deve ser um valor válido." });
      return;
    }

    const dateStr = format(date, "yyyy-MM-dd");
    const newStartTime = new Date(`${dateStr}T${startTime}:00`);
    const newEndTime = new Date(`${dateStr}T${endTime}:00`);

    // Validar datas criadas
    if (isNaN(newStartTime.getTime()) || isNaN(newEndTime.getTime())) {
      toast({ variant: "destructive", title: "Erro", description: "Horários inválidos." });
      return;
    }

    if (newEndTime <= newStartTime) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "O horário de fim deve ser maior que o de início.",
      });
      return;
    }

    const durationMinutes = Math.round((newEndTime.getTime() - newStartTime.getTime()) / 60000);

    if (durationMinutes > 24 * 60) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Duração máxima de 24 horas por entrada.",
      });
      return;
    }

    try {
      await createTimeEntry.mutateAsync({
        user_id: user.id,
        project_id: projectId,
        category,
        custom_category: customCategory.trim() || null,
        description: description.trim() || null,
        start_time: newStartTime.toISOString(),
        end_time: newEndTime.toISOString(),
        duration_minutes: durationMinutes,
        is_billable: isBillable,
        hourly_rate: hourlyRate ? parseFloat(hourlyRate) : null,
      });
      toast({
        title: "Entrada criada!",
        description: "A entrada foi registrada com sucesso.",
      });
      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível criar a entrada.",
      });
    }
  };

  // Memoiza cálculo da duração
  const displayDuration = useMemo(() => {
    if (!startTime || !endTime) return "-";
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    if (isNaN(sh) || isNaN(sm) || isNaN(eh) || isNaN(em)) return "-";
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;
    if (endMins <= startMins) return "-";
    const diff = endMins - startMins;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  }, [startTime, endTime]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar Entrada Manual</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Projeto *</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um projeto" />
              </SelectTrigger>
              <SelectContent>
                {projects?.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Categoria</Label>
            <CategorySelect
              value={category}
              customCategory={customCategory}
              onCategoryChange={setCategory}
              onCustomCategoryChange={setCustomCategory}
            />
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="O que você fez?"
            />
          </div>

          <div className="space-y-2">
            <Label>Data</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-2">
                  <CalendarDays className="h-4 w-4" />
                  {format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  locale={ptBR}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Início *</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Fim *</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Duração: <strong>{displayDuration}</strong>
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="billable-manual"
                checked={isBillable}
                onCheckedChange={(checked) => setIsBillable(checked as boolean)}
              />
              <Label htmlFor="billable-manual" className="cursor-pointer">Faturável</Label>
            </div>
            <div className="space-y-2">
              <Label>Taxa/Hora (R$)</Label>
              <Input
                type="number"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                placeholder="0,00"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={createTimeEntry.isPending}>
            {createTimeEntry.isPending ? "Salvando..." : "Adicionar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
